import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "../../lib/api";

interface Hold {
  holdId: string;
  bookId: string;
  title: string;
  author: string | null;
  coverUrl: string | null;
  isbn: string | null;
  userEmail: string;
  createdAt: string;
  expiresAt: string;
}

export function AdminHolds() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [flash, setFlash] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const query = useQuery<{ holds: Hold[] }, ApiError>({
    queryKey: ["admin", "holds", debounced],
    queryFn: () =>
      api<{ holds: Hold[] }>(
        debounced
          ? `/api/admin/holds?q=${encodeURIComponent(debounced)}`
          : "/api/admin/holds",
      ),
  });

  const holds = useMemo(() => query.data?.holds ?? [], [query.data]);
  const allSelected = holds.length > 0 && holds.every((h) => selected.has(h.holdId));
  const someSelected = !allSelected && holds.some((h) => selected.has(h.holdId));

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(holds.map((h) => h.holdId)));
  }
  function toggleOne(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  const bulk = useMutation({
    mutationFn: (holdIds: string[]) =>
      api<{ collected: number; bookIds: string[] }>("/api/admin/holds/bulk-collect", {
        method: "POST",
        body: { holdIds },
      }),
    onSuccess: (res) => {
      setFlash({ type: "ok", msg: t("admin.holdsBulkDone", { count: res.collected }) });
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "holds"] });
      qc.invalidateQueries({ queryKey: ["books"] });
    },
    onError: (err: ApiError) => setFlash({ type: "err", msg: err.message }),
  });

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleString();
  }

  return (
    <section aria-labelledby="holds-title" className="admin__panel">
      <h2 id="holds-title">{t("admin.holdsTitle")}</h2>
      <p>{t("admin.holdsIntro")}</p>

      {flash && (
        <div className={`inventory__flash inventory__flash--${flash.type}`} role="alert" aria-live="polite">
          {flash.msg}
        </div>
      )}

      <div className="holds__toolbar">
        <input
          type="search"
          className="holds__search"
          placeholder={t("admin.holdsSearchPlaceholder")}
          aria-label={t("admin.holdsSearchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={selected.size === 0 || bulk.isPending}
          onClick={() => bulk.mutate(Array.from(selected))}
        >
          {bulk.isPending
            ? t("admin.holdsBulkSubmitting")
            : t("admin.holdsBulkSubmit", { count: selected.size })}
        </button>
      </div>

      {query.isPending ? (
        <p>{t("common.loading")}</p>
      ) : query.isError ? (
        <p className="field__error" role="alert">{query.error.message}</p>
      ) : holds.length === 0 ? (
        <p className="surface" style={{ textAlign: "center" }}>{t("admin.holdsEmpty")}</p>
      ) : (
        <div className="holds-list" role="region" aria-label={t("admin.holdsTitle")}>
          <div className="holds-list__head">
            <label className="holds-list__select-all">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={toggleAll}
                aria-label={t("admin.holdsSelectAll")}
              />
              <span>{t("admin.holdsSelectAll")}</span>
            </label>
            <span className="holds-list__count">
              {t("admin.holdsCount", { count: holds.length })}
            </span>
          </div>

          <ul className="holds-list__items">
            {holds.map((h) => {
              const checked = selected.has(h.holdId);
              return (
                <li key={h.holdId} className={`hold-row ${checked ? "hold-row--checked" : ""}`}>
                  <label className="hold-row__check">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(h.holdId)}
                      aria-label={t("admin.holdsSelectOne", { title: h.title })}
                    />
                  </label>
                  <div className="hold-row__cover">
                    {h.coverUrl ? (
                      <img src={h.coverUrl} alt="" />
                    ) : (
                      <span aria-hidden="true">📖</span>
                    )}
                  </div>
                  <div className="hold-row__info">
                    <p className="hold-row__title">{h.title}</p>
                    {h.author && <p className="hold-row__author">{h.author}</p>}
                    <p className="hold-row__user">{h.userEmail}</p>
                  </div>
                  <div className="hold-row__meta">
                    <p>{t("admin.holdExpires")}: {fmtDate(h.expiresAt)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
